import React, { useState, useEffect } from 'react';
import { View, Text, TouchableOpacity, ActivityIndicator, ScrollView, Platform, StyleSheet, TextInput, Switch, Alert, KeyboardAvoidingView } from 'react-native';
import api from '../../services/api';
import AdminLayout from '../components/layout/AdminLayout';
import { Ionicons } from '@expo/vector-icons';

const VersionManagementScreen = ({ navigation }) => {
  const [settings, setSettings] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setIsLoading(true);
      const response = await api.get('/admin/settings');
      if (response.data.success) {
        const settingsMap = response.data.data.reduce((acc, s) => {
          acc[s.key] = s.value;
          return acc;
        }, {});
        setSettings(settingsMap);
      }
    } catch (err) {
      setError('Failed to load system settings');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      const settingsArray = Object.keys(settings)
        .filter(key => key.startsWith('APP_'))
        .map(key => ({
          key,
          value: settings[key]
        }));

      const response = await api.post('/admin/settings', { settings: settingsArray });
      
      if (response.data.success) {
        if (Platform.OS === 'web') {
          alert('🚀 Version control settings updated successfully!');
        } else {
          Alert.alert('Success', 'Version control settings updated successfully!');
        }
      }
    } catch (err) {
      console.error(err);
      alert('❌ Failed to save settings');
    } finally {
      setIsSaving(false);
    }
  };

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleBack = () => {
    navigation.navigate('AdminPanel');
  };

  if (isLoading) {
    return (
      <AdminLayout>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#6366F1" />
          <Text style={styles.loadingText}>Loading version control... ⏳</Text>
        </View>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <View style={styles.container}>
        {/* Header */}
        <View style={styles.header}>
          <TouchableOpacity onPress={handleBack} style={styles.backButton}>
            <Ionicons name="arrow-back" size={20} color="#64748B" />
            <Text style={styles.backButtonText}>Back</Text>
          </TouchableOpacity>
          <Text style={styles.headerTitle}>🚀 App Version Control</Text>
          <View style={{ width: 40 }} />
        </View>

        <ScrollView style={styles.mainContent}>
          <View style={styles.contentWrapper}>
            <View style={styles.pageHeader}>
              <Text style={styles.pageTitle}>Update Management 🔄</Text>
              <Text style={styles.pageSubtitle}>
                Control how and when your users update to the latest version.
              </Text>
            </View>

            <View style={styles.card}>
              <View style={styles.cardHeader}>
                <Ionicons name="settings-outline" size={20} color="#6366F1" />
                <Text style={styles.cardTitle}>Configuration</Text>
              </View>

              <View style={styles.cardBody}>
                {/* Latest Version */}
                <View style={styles.inputGroup}>
                  <Text style={styles.label}>Latest App Version (Play Store)</Text>
                  <View style={styles.inputContainer}>
                    <TextInput
                      style={styles.input}
                      value={settings.APP_LATEST_VERSION}
                      onChangeText={(val) => updateSetting('APP_LATEST_VERSION', val)}
                      placeholder="e.g. 1.9.50"
                    />
                  </View>
                  <Text style={styles.helperText}>This should match the version available in the Google Play Console.</Text>
                </View>

                {/* Min Version */}
                <View style={styles.inputGroup}>
                  <Text style={styles.label}>Minimum Required Version</Text>
                  <View style={styles.inputContainer}>
                    <TextInput
                      style={styles.input}
                      value={settings.APP_MIN_VERSION}
                      onChangeText={(val) => updateSetting('APP_MIN_VERSION', val)}
                      placeholder="e.g. 1.8.0"
                    />
                  </View>
                  <Text style={styles.helperText}>Any user below this version will be blocked from using the app until they update.</Text>
                </View>

                {/* Mandatory Toggle */}
                <View style={styles.toggleRow}>
                  <View style={{ flex: 1 }}>
                    <Text style={styles.label}>Force Immediate Update 🛡️</Text>
                    <Text style={styles.helperText}>If enabled, users will see the mandatory update screen immediately.</Text>
                  </View>
                  <Switch
                    value={settings.APP_UPDATE_MANDATORY === 'true'}
                    onValueChange={(val) => updateSetting('APP_UPDATE_MANDATORY', val.toString())}
                    trackColor={{ false: '#CBD5E1', true: '#6366F1' }}
                    thumbColor="#FFFFFF"
                  />
                </View>

                {/* Update Message */}
                <View style={styles.inputGroup}>
                  <Text style={styles.label}>Update Notice Message 📣</Text>
                  <View style={styles.textAreaContainer}>
                    <TextInput
                      style={styles.textArea}
                      value={settings.APP_UPDATE_MESSAGE}
                      onChangeText={(val) => updateSetting('APP_UPDATE_MESSAGE', val)}
                      placeholder="Enter update message..."
                      multiline={true}
                      numberOfLines={3}
                    />
                  </View>
                  <Text style={styles.helperText}>Shown to users when a new version is detected.</Text>
                </View>
              </View>

              <View style={styles.cardFooter}>
                <TouchableOpacity 
                  style={[styles.saveButton, isSaving && styles.saveButtonDisabled]}
                  onPress={handleSave}
                  disabled={isSaving}
                >
                  {isSaving ? (
                    <ActivityIndicator size="small" color="#FFFFFF" />
                  ) : (
                    <>
                      <Ionicons name="cloud-upload-outline" size={20} color="#FFFFFF" />
                      <Text style={styles.saveButtonText}>Apply Settings ✨</Text>
                    </>
                  )}
                </TouchableOpacity>
              </View>
            </View>

            {/* Info Section */}
            <View style={styles.infoCard}>
              <Ionicons name="information-circle" size={24} color="#3B82F6" />
              <View style={{ flex: 1, marginLeft: 12 }}>
                <Text style={styles.infoTitle}>How it works</Text>
                <Text style={styles.infoText}>
                  The app checks these settings every time it launches. If "Force Immediate Update" is active, it uses the Google Play Native Update UI. Otherwise, it downloads the update silently in the background using Expo Updates.
                </Text>
              </View>
            </View>
          </View>
        </ScrollView>
      </View>
    </AdminLayout>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F8FAFC'
  },
  header: {
    backgroundColor: '#FFFFFF',
    padding: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#E2E8F0',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  backButton: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  backButtonText: {
    marginLeft: 8,
    color: '#64748B',
    fontSize: 14,
    fontWeight: '500',
  },
  headerTitle: {
    fontSize: 16,
    fontWeight: '700',
    color: '#1E293B',
  },
  mainContent: {
    flex: 1
  },
  contentWrapper: {
    padding: 20,
    maxWidth: 800,
    alignSelf: 'center',
    width: '100%',
  },
  pageHeader: {
    marginBottom: 24,
  },
  pageTitle: {
    fontSize: 28,
    fontWeight: '800',
    color: '#0F172A',
    letterSpacing: -0.5,
  },
  pageSubtitle: {
    marginTop: 6,
    fontSize: 15,
    color: '#64748B',
    lineHeight: 22,
  },
  card: {
    backgroundColor: '#FFFFFF',
    borderRadius: 20,
    shadowColor: '#6366F1',
    shadowOffset: { width: 0, height: 10 },
    shadowOpacity: 0.05,
    shadowRadius: 20,
    elevation: 3,
    borderWidth: 1,
    borderColor: '#F1F5F9',
    overflow: 'hidden'
  },
  cardHeader: {
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#F1F5F9',
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
  },
  cardTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#1E293B',
  },
  cardBody: {
    padding: 20,
  },
  inputGroup: {
    marginBottom: 20,
  },
  label: {
    fontSize: 14,
    fontWeight: '700',
    color: '#475569',
    marginBottom: 8,
  },
  inputContainer: {
    backgroundColor: '#F8FAFC',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 12,
    paddingHorizontal: 16,
    height: 48,
    justifyContent: 'center',
  },
  input: {
    fontSize: 15,
    color: '#1E293B',
    fontWeight: '600',
  },
  textAreaContainer: {
    backgroundColor: '#F8FAFC',
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 12,
    padding: 12,
    minHeight: 80,
  },
  textArea: {
    fontSize: 15,
    color: '#1E293B',
    fontWeight: '500',
    lineHeight: 22,
  },
  helperText: {
    fontSize: 12,
    color: '#94A3B8',
    marginTop: 6,
    lineHeight: 18,
  },
  toggleRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 24,
    paddingVertical: 12,
    borderTopWidth: 1,
    borderBottomWidth: 1,
    borderColor: '#F1F5F9',
  },
  cardFooter: {
    padding: 20,
    backgroundColor: '#F8FAFC',
    borderTopWidth: 1,
    borderTopColor: '#F1F5F9',
  },
  saveButton: {
    backgroundColor: '#6366F1',
    flexDirection: 'row',
    height: 52,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    shadowColor: '#6366F1',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.25,
    shadowRadius: 10,
    elevation: 4,
  },
  saveButtonDisabled: {
    opacity: 0.7,
  },
  saveButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '700',
  },
  infoCard: {
    marginTop: 24,
    backgroundColor: '#EFF6FF',
    borderRadius: 16,
    padding: 20,
    flexDirection: 'row',
    alignItems: 'flex-start',
    borderWidth: 1,
    borderColor: '#DBEAFE',
  },
  infoTitle: {
    fontSize: 15,
    fontWeight: '700',
    color: '#1E40AF',
    marginBottom: 4,
  },
  infoText: {
    fontSize: 13,
    color: '#3B82F6',
    lineHeight: 20,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    marginTop: 12,
    color: '#64748B',
    fontWeight: '500',
  }
});

export default VersionManagementScreen;
